import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useLoading, useAuth } from '../check_login';
import { IoArrowBack } from "react-icons/io5";
import { TbEdit, TbTrash } from 'react-icons/tb';
import { motion } from 'framer-motion';
import '../../../assets/css/brand.css'

const CreateBrandForm = ({ pageTitle }) => {
  const navigate = useNavigate();
  const { showLoading, hideLoading } = useLoading();
  const { user: currentUser } = useAuth();
  const [brandData, setBrandData] = useState({
    brandName: '',
    disclaimer_marketing: '',
    viewInBrowserURL: '',
    frameColor: '',
    backgroundColor: '',
    buttonGenralColor: '',
    buttonTextColor: '',
    smalldisclaimer: '',
    logo: '',
    borderRadius: '',
  });

  const [editModalShow, setEditModalShow] = useState(false);
  const [editingBrand, setEditingBrand] = useState(null);
  const [languages, setLanguages] = useState([]);
  const [selectedLanguages, setSelectedLanguages] = useState([]);
  const [selectedLanguagesInEditModal, setSelectedLanguagesInEditModal] = useState([]);
  const [error, setError] = useState('');
  const [brands, setBrands] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [expandedBrands, setExpandedBrands] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});
  const [deleteModalShow, setDeleteModalShow] = useState(false);
  const [brandToDelete, setBrandToDelete] = useState(null);
  const token = localStorage.getItem('token');

  useEffect(() => {
    const fetchLanguages = async () => {
      try {
        const response = await axios.get('/brand/languages/active', {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setLanguages(response.data);
      } catch (error) {
        console.error('Error fetching languages:', error);
      }
    };

    const fetchBrands = async () => {
      try {
        const response = await axios.get('/brand/list', {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const updatedBrands = response.data.map((brand) => ({
          ...brand,
          languages: brand.languages || [],
        }));

        setBrands(updatedBrands);
      } catch (error) {
        console.error('Error fetching brands:', error);
      }
    };

    fetchLanguages();
    fetchBrands();
  }, []);

  const handleChange = (e) => {
    setBrandData({ ...brandData, [e.target.name]: e.target.value });
  };

  const handleEditChange = (e) => {
    setEditingBrand({ ...editingBrand, [e.target.name]: e.target.value });
  };

  const openEditModal = (brand) => {
    setEditingBrand(brand);
    const existingLanguages = (brand.langs || []).map((lang) => ({
      languageId: lang.language,
      languageName: lang.languageName,
      logoLink: lang.logoLink || '',
    }));
    setSelectedLanguagesInEditModal(existingLanguages);
    setEditModalShow(true);
  };

  const handleLogoLinkChange = (e, languageId, isEdit = false) => {
    if (isEdit) {
      setSelectedLanguagesInEditModal(
        selectedLanguagesInEditModal.map((lang) =>
          lang.languageId === languageId ? { ...lang, logoLink: e.target.value } : lang
        )
      );
    } else {
      setSelectedLanguages(
        selectedLanguages.map((lang) =>
          lang.languageId === languageId ? { ...lang, logoLink: e.target.value } : lang
        )
      );
    }
  };

  const toggleLanguageSelectionForCreate = (language) => {
    const isSelected = selectedLanguages.some(
      (lang) => lang.languageId === language._id
    );
    if (isSelected) {
      setSelectedLanguages(
        selectedLanguages.filter((lang) => lang.languageId !== language._id)
      );
    } else {
      setSelectedLanguages([
        ...selectedLanguages,
        { languageId: language._id, languageName: language.languageName, code: language.code, logoLink: '' },
      ]);
    }
  };

  const toggleLanguageSelectionForEdit = (language) => {
    const isSelected = selectedLanguagesInEditModal.some(
      (lang) => lang.languageId === language._id
    );
    if (isSelected) {
      setSelectedLanguagesInEditModal(
        selectedLanguagesInEditModal.filter((lang) => lang.languageId !== language._id)
      );
    } else {
      setSelectedLanguagesInEditModal([
        ...selectedLanguagesInEditModal,
        { languageId: language._id, languageName: language.languageName, code: language.code, logoLink: '' },
      ]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    setFieldErrors({});
    showLoading();

    try {
      if (!token) {
        setError('You are not logged in');
        hideLoading();
        setIsSubmitting(false);
        return;
      }

      // Validation
      const errors = {};
      
      if (!brandData.brandName || brandData.brandName.trim() === '') {
        errors.brandName = 'Brand name is required';
      }

      if (selectedLanguages.length === 0) {
        errors.languages = 'Please select at least one language';
      }

      // Validate number fields
      if (brandData.borderRadius !== '' && !/^\d+$/.test(String(brandData.borderRadius).trim())) {
        errors.borderRadius = 'Border radius must be a whole number (e.g., 5)';
      }

      // Validate color fields (hex codes)
      const hexColorRegex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;
      if (brandData.frameColor && !hexColorRegex.test(brandData.frameColor.trim())) {
        errors.frameColor = 'Frame color must be a valid hex code (e.g., #FF5733)';
      }
      if (brandData.backgroundColor && !hexColorRegex.test(brandData.backgroundColor.trim())) {
        errors.backgroundColor = 'Background color must be a valid hex code (e.g., #FF5733)';
      }
      if (brandData.buttonGenralColor && !hexColorRegex.test(brandData.buttonGenralColor.trim())) {
        errors.buttonGenralColor = 'Button color must be a valid hex code (e.g., #FF5733)';
      }
      if (brandData.buttonTextColor && !hexColorRegex.test(brandData.buttonTextColor.trim())) {
        errors.buttonTextColor = 'Button text color must be a valid hex code (e.g., #FF5733)';
      }

      // Validate URL fields
      const urlRegex = /^https?:\/\/.+/;
      if (brandData.viewInBrowserURL && !urlRegex.test(brandData.viewInBrowserURL.trim())) {
        errors.viewInBrowserURL = 'View in browser URL must be a valid URL (starting with http:// or https://)';
      }
      if (brandData.logo && !urlRegex.test(brandData.logo.trim())) {
        errors.logo = 'Logo URL must be a valid URL (starting with http:// or https://)';
      }

      if (Object.keys(errors).length > 0) {
        setFieldErrors(errors);
        setError('Please fix the validation errors below');
        hideLoading();
        setIsSubmitting(false);
        return;
      }

      const response = await axios.post(
        '/brand/create',
        { ...brandData, selectedLanguages },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 200) {
        navigate('/');
      }
    } catch (err) {
      console.error('Error details:', err.response || err);
      
      // Extract validation errors
      if (err.response?.data?.errors) {
        const validationErrors = err.response.data.errors;
        const serverErrors = {};
        const errorMessages = [];
        
        Object.keys(validationErrors).forEach(key => {
          const error = validationErrors[key];
          let errorMsg = '';
          
          if (error.message) {
            errorMsg = error.message;
          } else if (error.kind === 'required') {
            errorMsg = `${key} is required`;
          } else if (error.kind === 'Number') {
            errorMsg = `${key} must be a number`;
          } else if (error.kind === 'String') {
            errorMsg = `${key} must be a valid text`;
          } else {
            errorMsg = `${key}: ${error.kind || 'validation error'}`;
          }
          
          serverErrors[key] = errorMsg;
          errorMessages.push(errorMsg);
        });
        
        setFieldErrors(serverErrors);
        setError(errorMessages.join('; '));
      } else if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else if (err.response?.data?._message) {
        setError(err.response.data._message);
      } else {
        setError('Error creating brand. Please check all required fields.');
      }
    } finally {
      hideLoading();
      setIsSubmitting(false);
    }
  };

  const handleEditSubmit = async () => {
    setIsSubmitting(true);
    setError('');
    showLoading();
    try {
      // Validation
      if (!editingBrand.brandName || editingBrand.brandName.trim() === '') {
        setError('Brand name is required');
        hideLoading();
        setIsSubmitting(false);
        return;
      }

      if (selectedLanguagesInEditModal.length === 0) {
        setError('Please select at least one language');
        hideLoading();
        setIsSubmitting(false);
        return;
      }

      editingBrand.langs = selectedLanguagesInEditModal.map((lang) => ({
        language: lang.languageId,
        languageName: lang.languageName,
        logoLink: lang.logoLink,
      }));

      const response = await axios.put(
        `/brand/update/${editingBrand._id}`,
        editingBrand,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 200) {
        const updatedBrands = brands.map((brand) =>
          brand._id === editingBrand._id ? editingBrand : brand
        );
        setBrands(updatedBrands);
        setEditModalShow(false);
      }
    } catch (error) {
      console.error('Error updating brand:', error);
      
      // Extract validation errors
      if (error.response?.data?.errors) {
        const validationErrors = error.response.data.errors;
        const errorMessages = Object.keys(validationErrors).map(key => {
          const err = validationErrors[key];
          if (err.message) {
            return err.message;
          }
          return `${key}: ${err.kind || 'validation error'}`;
        });
        setError(errorMessages.join(', '));
      } else if (error.response?.data?.message) {
        setError(error.response.data.message);
      } else if (error.response?.data?._message) {
        setError(error.response.data._message);
      } else {
        setError('Error updating brand. Please check all required fields.');
      }
    } finally {
      hideLoading();
      setIsSubmitting(false);
    }
  };

  const handleDeleteBrand = (brand) => {
    setBrandToDelete(brand);
    setDeleteModalShow(true);
  };

  const confirmDeleteBrand = async () => {
    if (!brandToDelete) return;

    setIsSubmitting(true);
    showLoading();
    try {
      const response = await axios.delete(`/brand/delete/${brandToDelete._id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.status === 200) {
        setBrands(brands.filter(brand => brand._id !== brandToDelete._id));
        setDeleteModalShow(false);
        setBrandToDelete(null);
        setError('');
      }
    } catch (error) {
      console.error('Error deleting brand:', error);
      setError(error.response?.data?.message || 'Error deleting brand');
    } finally {
      hideLoading();
      setIsSubmitting(false);
    }
  };

  // Check if user has permission to delete brands
  const canDeleteBrands = currentUser?.isAdmin || currentUser?.role?.permissions?.canDeleteBrands;

  return (
    <div className="add-creative-container">
      {/* Header Section */}
      <div className="header-section">
        <motion.button 
          className="back-button"
          onClick={() => navigate(-1)}
          whileHover={{ scale: 1.05 }}
        >
          <IoArrowBack /> Back
        </motion.button>
        <h1 className="page-title">{pageTitle}</h1>
      </div>

      {/* Error Message */}
      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <div className="brands-container">
        {/* Brands Grid */}
        <div className="brands-grid">
          {brands.map((brand) => (
            <motion.div 
              key={brand._id} 
              className="brand-card"
            >
              <div className="brand-logo-container">
                {brand.logo && (
                  <img 
                    src={brand.logo} 
                    alt={`${brand.brandName} logo`} 
                    className="brand-logo" 
                  />
                )}
              </div>
              <h3 className="brand-name">{brand.brandName}</h3>
              <div className="languages-section">
                <h4>Languages:</h4>
                <div className="languages-list">
                  {brand.langs && brand.langs.length > 0 ? (
                    <>
                      {(expandedBrands[brand._id] ? brand.langs : brand.langs.slice(0, 2)).map((language) => (
                        <span key={language._id} className="language-tag">
                          {languages.find(l => l._id === language.language)?.code?.toUpperCase() || language.languageName}
                        </span>
                      ))}
                      {brand.langs.length > 2 && (
                        <button
                          className="toggle-languages-btn"
                          onClick={() => setExpandedBrands(prev => ({
                            ...prev,
                            [brand._id]: !prev[brand._id]
                          }))}
                        >
                          {expandedBrands[brand._id] ? 'Less' : `More (${brand.langs.length - 2})`}
                        </button>
                      )}
                    </>
                  ) : (
                    <p>No languages available</p>
                  )}
                </div>
              </div>
              <div className="brand-card-actions">
                <motion.button
                  className="brand-modify-btn"
                  onClick={() => openEditModal(brand)}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  title="Edit"
                >
                  <TbEdit />
                </motion.button>
                {canDeleteBrands && (
                  <motion.button
                    className="brand-remove-btn"
                    onClick={() => handleDeleteBrand(brand)}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    title="Delete"
                  >
                    <TbTrash />
                  </motion.button>
                )}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Create Brand Form */}
        <div className="create-brand-form">
          <h2 className="form-title">Add New Brand</h2>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="input-label required">Brand Name</label>
              <input
                type="text"
                name="brandName"
                className={`form-input ${fieldErrors.brandName ? 'error' : ''}`}
                placeholder="Enter brand name"
                value={brandData.brandName}
                onChange={handleChange}
                required
              />
              {fieldErrors.brandName && <span className="field-error">{fieldErrors.brandName}</span>}
            </div>

            <div className="form-group">
              <label className="input-label required">Select Languages</label>
              {fieldErrors.languages && <span className="field-error">{fieldErrors.languages}</span>}
              <div className="language-selector">
                {languages.map((language) => (
                  <motion.div
                    key={language._id}
                    className={`language-option ${selectedLanguages.some(
                      (lang) => lang.languageId === language._id
                    ) ? 'selected' : ''}`}
                    onClick={() => toggleLanguageSelectionForCreate(language)}
                    whileHover={{ scale: 1.02 }}
                  >
                    {language.code?.toUpperCase() || language.languageName}
                  </motion.div>
                ))}
              </div>
            </div>

            {selectedLanguages.map((lang) => (
              <div className="form-group" key={lang.languageId}>
                <label className="input-label">{lang.code?.toUpperCase() || lang.languageName} Logo Link</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="Enter logo link"
                  value={lang.logoLink}
                  onChange={(e) => handleLogoLinkChange(e, lang.languageId)}
                />
              </div>
            ))}

            <div className="form-group">
              <label className="input-label required">Disclaimer</label>
              <input
                type="text"
                name="disclaimer_marketing"
                className={`form-input ${fieldErrors.disclaimer_marketing ? 'error' : ''}`}
                placeholder="Enter disclaimer"
                value={brandData.disclaimer_marketing}
                onChange={handleChange}
                required
              />
              {fieldErrors.disclaimer_marketing && <span className="field-error">{fieldErrors.disclaimer_marketing}</span>}
            </div>

            <div className="form-group">
              <label className="input-label required">View in Browser URL</label>
              <input
                type="text"
                name="viewInBrowserURL"
                className={`form-input ${fieldErrors.viewInBrowserURL ? 'error' : ''}`}
                placeholder="Enter view in browser URL (e.g., https://example.com)"
                value={brandData.viewInBrowserURL}
                onChange={handleChange}
                required
              />
              {fieldErrors.viewInBrowserURL && <span className="field-error">{fieldErrors.viewInBrowserURL}</span>}
            </div>

            <div className="form-group">
              <label className="input-label required">Frame Color</label>
              <input
                type="text"
                name="frameColor"
                className={`form-input ${fieldErrors.frameColor ? 'error' : ''}`}
                placeholder="Enter frame color (e.g., #FF5733)"
                value={brandData.frameColor}
                onChange={handleChange}
                required
              />
              {fieldErrors.frameColor && <span className="field-error">{fieldErrors.frameColor}</span>}
            </div>

            <div className="form-group">
              <label className="input-label required">Background Color</label>
              <input
                type="text"
                name="backgroundColor"
                className={`form-input ${fieldErrors.backgroundColor ? 'error' : ''}`}
                placeholder="Enter background color (e.g., #FF5733)"
                value={brandData.backgroundColor}
                onChange={handleChange}
                required
              />
              {fieldErrors.backgroundColor && <span className="field-error">{fieldErrors.backgroundColor}</span>}
            </div>

            <div className="form-group">
              <label className="input-label required">Button General Color</label>
              <input
                type="text"
                name="buttonGenralColor"
                className={`form-input ${fieldErrors.buttonGenralColor ? 'error' : ''}`}
                placeholder="Enter button general color (e.g., #FF5733)"
                value={brandData.buttonGenralColor}
                onChange={handleChange}
                required
              />
              {fieldErrors.buttonGenralColor && <span className="field-error">{fieldErrors.buttonGenralColor}</span>}
            </div>

            <div className="form-group">
              <label className="input-label required">Button Text Color</label>
              <input
                type="text"
                name="buttonTextColor"
                className={`form-input ${fieldErrors.buttonTextColor ? 'error' : ''}`}
                placeholder="Enter button text color (e.g., #FFFFFF)"
                value={brandData.buttonTextColor}
                onChange={handleChange}
                required
              />
              {fieldErrors.buttonTextColor && <span className="field-error">{fieldErrors.buttonTextColor}</span>}
            </div>

            <div className="form-group">
              <label className="input-label required">Small Disclaimer</label>
              <input
                type="text"
                name="smalldisclaimer"
                className={`form-input ${fieldErrors.smalldisclaimer ? 'error' : ''}`}
                placeholder="Enter small disclaimer"
                value={brandData.smalldisclaimer}
                onChange={handleChange}
                required
              />
              {fieldErrors.smalldisclaimer && <span className="field-error">{fieldErrors.smalldisclaimer}</span>}
            </div>

            <div className="form-group">
              <label className="input-label required">Logo URL</label>
              <input
                type="text"
                name="logo"
                className={`form-input ${fieldErrors.logo ? 'error' : ''}`}
                placeholder="Enter logo URL (e.g., https://example.com/logo.png)"
                value={brandData.logo}
                onChange={handleChange}
                required
              />
              {fieldErrors.logo && <span className="field-error">{fieldErrors.logo}</span>}
            </div>

            <div className="form-group">
              <label className="input-label required">Border Radius (Buttons)</label>
              <input
                type="number"
                min="0"
                step="1"
                name="borderRadius"
                className={`form-input ${fieldErrors.borderRadius ? 'error' : ''}`}
                placeholder="Enter border radius in px (e.g., 5)"
                value={brandData.borderRadius}
                onChange={handleChange}
                required
              />
              {fieldErrors.borderRadius && <span className="field-error">{fieldErrors.borderRadius}</span>}
            </div>

            <div className="form-actions">
              <motion.button 
                type="submit" 
                className="submit-button"
                disabled={isSubmitting}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
              >
                {isSubmitting ? (
                  <>
                    <span className="button-spinner"></span>
                    Creating...
                  </>
                ) : (
                  'Create Brand'
                )}
              </motion.button>
            </div>
          </form>
        </div>
      </div>

      {/* Edit Modal */}
      {editModalShow && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Edit Brand</h2>
              <button 
                className="modal-close"
                onClick={() => setEditModalShow(false)}
              >
                &times;
              </button>
            </div>
            <div className="modal-body">
              {editingBrand && (
                <form>
                  <div className="form-group">
                    <label className="input-label">Brand Name</label>
                    <input
                      type="text"
                      name="brandName"
                      className="form-input"
                      placeholder="Enter brand name"
                      value={editingBrand.brandName}
                      onChange={handleEditChange}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label className="input-label">Select Languages</label>
                    <div className="language-selector">
                      {languages.map((language) => (
                        <motion.div
                          key={language._id}
                          className={`language-option ${selectedLanguagesInEditModal.some(
                            (lang) => lang.languageId === language._id
                          ) ? 'selected' : ''}`}
                          onClick={() => toggleLanguageSelectionForEdit(language)}
                          whileHover={{ scale: 1.02 }}
                        >
                          {language.code?.toUpperCase() || language.languageName}
                        </motion.div>
                      ))}
                    </div>
                  </div>

                  {selectedLanguagesInEditModal.map((lang) => (
                    <div className="form-group" key={lang.languageId}>
                      <label className="input-label">{lang.code?.toUpperCase() || lang.languageName} Logo Link</label>
                      <input
                        type="text"
                        className="form-input"
                        placeholder="Enter logo link"
                        value={lang.logoLink}
                        onChange={(e) => handleLogoLinkChange(e, lang.languageId, true)}
                      />
                    </div>
                  ))}

                  <div className="form-group">
                    <label className="input-label">Disclaimer</label>
                    <input
                      type="text"
                      name="disclaimer_marketing"
                      className="form-input"
                      placeholder="Enter disclaimer"
                      value={editingBrand.disclaimer_marketing}
                      onChange={handleEditChange}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label className="input-label">View in Browser URL</label>
                    <input
                      type="text"
                      name="viewInBrowserURL"
                      className="form-input"
                      placeholder="Enter view in browser URL"
                      value={editingBrand.viewInBrowserURL}
                      onChange={handleEditChange}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label className="input-label">Frame Color</label>
                    <input
                      type="text"
                      name="frameColor"
                      className="form-input"
                      placeholder="Enter frame color (hex code)"
                      value={editingBrand.frameColor}
                      onChange={handleEditChange}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label className="input-label">Background Color</label>
                    <input
                      type="text"
                      name="backgroundColor"
                      className="form-input"
                      placeholder="Enter background color (hex code)"
                      value={editingBrand.backgroundColor}
                      onChange={handleEditChange}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label className="input-label">Button General Color</label>
                    <input
                      type="text"
                      name="buttonGenralColor"
                      className="form-input"
                      placeholder="Enter button general color (hex code)"
                      value={editingBrand.buttonGenralColor}
                      onChange={handleEditChange}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label className="input-label">Button Text Color</label>
                    <input
                      type="text"
                      name="buttonTextColor"
                      className="form-input"
                      placeholder="Enter button text color (hex code)"
                      value={editingBrand.buttonTextColor}
                      onChange={handleEditChange}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label className="input-label">Small Disclaimer</label>
                    <input
                      type="text"
                      name="smalldisclaimer"
                      className="form-input"
                      placeholder="Enter small disclaimer"
                      value={editingBrand.smalldisclaimer}
                      onChange={handleEditChange}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label className="input-label">Logo URL</label>
                    <input
                      type="text"
                      name="logo"
                      className="form-input"
                      placeholder="Enter logo URL"
                      value={editingBrand.logo}
                      onChange={handleEditChange}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label className="input-label">Border Radius (Buttons)</label>
                    <input
                      type="number"
                      min="0"
                      step="1"
                      name="borderRadius"
                      className="form-input"
                      placeholder="Enter border radius in px (e.g., 5)"
                      value={editingBrand.borderRadius}
                      onChange={handleEditChange}
                      required
                    />
                  </div>
                </form>
              )}
            </div>
            <div className="modal-footer">
              <motion.button
                className="cancel-button"
                onClick={() => setEditModalShow(false)}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
              >
                Cancel
              </motion.button>
              <motion.button
                className="save-button"
                onClick={handleEditSubmit}
                disabled={isSubmitting}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
              >
                {isSubmitting ? (
                  <>
                    <span className="button-spinner"></span>
                    Saving...
                  </>
                ) : (
                  'Save'
                )}
              </motion.button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModalShow && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Delete Brand</h2>
              <button 
                className="modal-close" 
                onClick={() => setDeleteModalShow(false)}
              >
                ✕
              </button>
            </div>
            <div className="modal-body">
              <p className="delete-confirmation-message">
                Are you sure you want to delete the brand <strong>{brandToDelete?.brandName}</strong>?
              </p>
              <p className="delete-warning-text">This action cannot be undone.</p>
            </div>
            <div className="modal-footer">
              <motion.button
                className="cancel-button"
                onClick={() => {
                  setDeleteModalShow(false);
                  setBrandToDelete(null);
                }}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
              >
                Cancel
              </motion.button>
              <motion.button
                className="delete-confirm-button"
                onClick={confirmDeleteBrand}
                disabled={isSubmitting}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
              >
                {isSubmitting ? 'Deleting...' : 'Delete Brand'}
              </motion.button>
            </div>
          </div>
        </div>
      )}

      {/* Loading Overlay */}
      {isSubmitting && (
        <div className="loading-overlay">
          <div className="spinner"></div>
        </div>
      )}

    </div>
  );
};

export default CreateBrandForm;